import { API_ENDPOINTS } from '@/constants/api';
import { EntityForm } from '@/shared/components/EntityForm';
import { PageLayout } from '@/shared/components/PageLayout';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import {
  CBadge,
  CFormCheck,
  CFormInput,
  CNav,
  CNavItem,
  CNavLink,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableRow,
} from '@coreui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rolesApi } from '../api';
import { useOwnRoleLevel } from '../hooks/useOwnRoleLevel';
import { useRoleForm } from '../hooks/useRoleForm';
import type { PermissionOption, RoleData } from '../types';

export const RoleForm = () => {
  const navigate = useNavigate();
  const { entity: role, error, handleSubmit, isEdit } = useRoleForm();
  const [data, setData] = useState<Omit<RoleData, 'level'> & { level: string }>(
    role
      ? { name: role.name, level: String(role.level), permissions: role.permissions }
      : { name: '', level: '0', permissions: [] },
  );
  const [activeTab, setActiveTab] = useState('Customers');
  const [autoPermissions, setAutoPermissions] = useState<string[]>([]);
  const [previousRole, setPreviousRole] = useState(role);
  const [catalog, setCatalog] = useState<PermissionOption[]>([]);
  const [catalogError, setCatalogError] = useState('');
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    level?: string;
    permissions?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);
  const { ownLevel: loadedOwnLevel, error: levelError } = useOwnRoleLevel();
  const loaded = catalogLoaded && loadedOwnLevel !== null;

  if (role !== previousRole) {
    setPreviousRole(role);
    setAutoPermissions([]);
    setData(
      role
        ? { name: role.name, level: String(role.level), permissions: role.permissions }
        : { name: '', level: '0', permissions: [] },
    );
  }

  useEffect(() => {
    let active = true;
    void rolesApi
      .getPermissions()
      .then((options) => {
        if (active) {
          setCatalog(options);
          setCatalogLoaded(true);
        }
      })
      .catch(() => {
        if (active) setCatalogError('Failed to load permissions.');
      });
    return () => {
      active = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (validationAttempt === 0) return;
    const invalid = fieldsetRef.current?.querySelector<HTMLElement>(
      '.is-invalid, .invalid-feedback.d-block',
    );
    if (!invalid) return;
    invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    invalid.focus({ preventScroll: true });
  }, [validationAttempt]);

  const required = new Set(
    catalog
      .filter((permission) => data.permissions.includes(permission.key))
      .flatMap((permission) => permission.requires),
  );
  const groups = ['Customers', 'Products', 'Promotions', 'Users', 'Logs', 'System'].filter(
    (group) => catalog.some((permission) => permission.group === group),
  );
  const activeGroup = groups.includes(activeTab) ? activeTab : groups[0];
  const ownLevel = loadedOwnLevel ?? 0;
  const maxLevel = ownLevel - 1;

  const toggle = (option: PermissionOption, checked: boolean) => {
    const selected = new Set(data.permissions);
    const automatic = new Set(autoPermissions);
    if (checked) {
      selected.add(option.key);
      option.requires.forEach((key) => {
        if (!selected.has(key)) automatic.add(key);
        selected.add(key);
      });
    } else {
      selected.delete(option.key);
      option.requires.forEach((key) => {
        const stillRequired = catalog.some(
          (permission) => selected.has(permission.key) && permission.requires.includes(key),
        );
        if (!stillRequired && automatic.has(key)) {
          selected.delete(key);
          automatic.delete(key);
        }
      });
    }
    setAutoPermissions([...automatic]);
    setData((previous) => ({ ...previous, permissions: [...selected] }));
  };

  return (
    <PageLayout>
      <EntityForm
        title={isEdit ? 'Edit role' : 'Add new role'}
        submitButtonLabel={isEdit ? 'Update role' : 'Add new role'}
        submitButtonFirst
        onCancel={() => {
          void navigate('/users/roles');
        }}
        isEdit={isEdit}
        error={error || catalogError || levelError}
        listEndpoint={API_ENDPOINTS.ROLES.LIST}
        requiredPermission={USER_PERMISSIONS.UserRolesEdit}
        submitButtonDisabled={saving || !loaded || (isEdit && !role)}
        onSubmit={(event) => {
          event.preventDefault();
          const level = data.level.trim();
          const errors: typeof fieldErrors = {};
          if (!data.name.trim()) errors.name = 'Role title is required.';
          if (level === '') errors.level = 'Role level is required.';
          else if (!Number.isInteger(Number(level)))
            errors.level = 'Role level must be a whole number.';
          else if (Number(level) < 0) errors.level = 'Role level must not be negative.';
          else if (Number(level) > maxLevel)
            errors.level = `Role level must be lower than your own role level (${ownLevel}).`;
          if (data.permissions.length === 0) errors.permissions = 'Select at least one permission.';
          setFieldErrors(errors);
          if (Object.keys(errors).length > 0) {
            setValidationAttempt((attempt) => attempt + 1);
            return;
          }
          setSaving(true);
          void handleSubmit({ ...data, name: data.name.trim(), level: Number(data.level) }).finally(
            () => setSaving(false),
          );
        }}
      >
        <fieldset ref={fieldsetRef} disabled={saving || !loaded}>
          <div className="mb-3">
            <CFormInput
              label={
                <>
                  Role title <span className="text-danger">*</span>
                </>
              }
              name="name"
              maxLength={255}
              invalid={!!fieldErrors.name}
              feedbackInvalid={fieldErrors.name}
              value={data.name}
              onChange={(event) =>
                setData((previous) => ({ ...previous, name: event.target.value }))
              }
            />
          </div>
          <div className="mb-3">
            <CFormInput
              label={
                <>
                  Level <span className="text-danger">*</span>
                </>
              }
              name="level"
              type="number"
              step="any"
              invalid={!!fieldErrors.level}
              feedbackInvalid={fieldErrors.level}
              value={data.level}
              onChange={(event) =>
                setData((previous) => ({ ...previous, level: event.target.value }))
              }
            />
          </div>
          <section className="role-form-permissions" aria-label="Role permissions">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
              <h5 className="mb-0">Permissions</h5>
              <span
                className="text-body-secondary small"
                role="status"
                aria-label="Selected permissions"
              >
                {data.permissions.length} selected
              </span>
            </div>
            {fieldErrors.permissions && (
              <div className="invalid-feedback d-block mb-3">{fieldErrors.permissions}</div>
            )}
            <CNav
              variant="tabs"
              role="tablist"
              aria-label="Permission groups"
              className="role-form-permissions__tabs"
            >
              {groups.map((group) => (
                <CNavItem key={group}>
                  <CNavLink
                    as="button"
                    type="button"
                    role="tab"
                    id={`permission-tab-${group}`}
                    aria-label={group}
                    aria-selected={activeGroup === group}
                    aria-controls="permission-panel"
                    active={activeGroup === group}
                    onClick={() => setActiveTab(group)}
                  >
                    {group}
                    <CBadge
                      className="role-form-permissions__count"
                      shape="rounded-pill"
                      aria-hidden="true"
                    >
                      {
                        catalog.filter(
                          (option) =>
                            option.group === group && data.permissions.includes(option.key),
                        ).length
                      }
                    </CBadge>
                  </CNavLink>
                </CNavItem>
              ))}
            </CNav>
            {activeGroup && (
              <div
                className="role-form-permissions__panel"
                role="tabpanel"
                id="permission-panel"
                aria-labelledby={`permission-tab-${activeGroup}`}
              >
                <CTable responsive className="mb-0" aria-label="Permissions">
                  <CTableBody>
                    {catalog
                      .filter((option) => option.group === activeGroup)
                      .sort((left, right) => left.key.localeCompare(right.key))
                      .map((option) => (
                        <CTableRow key={option.key}>
                          <CTableDataCell className="text-center align-middle">
                            <CFormCheck
                              id={option.key}
                              aria-label={option.key}
                              checked={data.permissions.includes(option.key)}
                              disabled={required.has(option.key)}
                              onChange={(event) => toggle(option, event.target.checked)}
                            />
                          </CTableDataCell>
                          <CTableDataCell className="align-middle fw-medium">
                            <label htmlFor={option.key} style={{ cursor: 'pointer' }}>
                              {option.key}
                            </label>
                          </CTableDataCell>
                          <CTableDataCell className="align-middle text-body-secondary">
                            {option.description}
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                  </CTableBody>
                </CTable>
              </div>
            )}
          </section>
        </fieldset>
      </EntityForm>
    </PageLayout>
  );
};
